import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import ShareButton from "@/components/ShareButton";
import EventDiscussion from "@/components/EventDiscussion";
import TalkVibes from "@/components/TalkVibes";
import EventMemoriesUploader from "@/components/EventMemoriesUploader";
import InviteAlumniDialog from "@/components/InviteAlumniDialog";
import EventPhotoLightbox from "@/components/EventPhotoLightbox";
import {
  ArrowLeft, CalendarStar, MapPin, VideoCamera, Users, Clock, Heart, Check,
  Globe, Ticket, Trophy, HandHeart, GoogleLogo, DownloadSimple, Buildings, EnvelopeSimple, User, Images,
  CopySimple, ArrowsClockwise, ThumbsUp, ChatCircle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

function fmtDate(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString("en", { weekday: "short", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function gcalStamp(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

export default function EventDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [e, setE] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const load = () => api.get(`/public/events/${id}`).then((r) => setE(r.data)).catch(() => setE(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useSeo(e && e !== false ? e.title : "Event", e && e !== false ? e.description : null, e && e !== false ? e.cover : null);

  if (e === null) return <div className="p-16 text-center text-muted-foreground">Loading…</div>;
  if (e === false) return <div className="p-16 text-center text-muted-foreground">Event not found.</div>;

  const requireLogin = () => toast.error("Please sign in to continue");
  const duplicate = async () => {
    try {
      const r = await api.post(`/events/${id}/duplicate`);
      toast.success("Event duplicated — update the dates & venue, then it's live");
      navigate(`/events/${r.data.id}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const register = async () => {
    if (!user) return requireLogin();
    try {
      const status = e.my_rsvp ? "cancel" : "registered";
      await api.post(`/events/${id}/register`, null, { params: { status } });
      toast.success(status === "cancel" ? "Registration cancelled" : "You're registered! 🎉");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const interested = async () => {
    if (!user) return requireLogin();
    try { const r = await api.post(`/events/${id}/interested`); toast.success(r.data.interested ? "Marked interested" : "Removed"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const volunteer = async () => {
    if (!user) return requireLogin();
    try { const r = await api.post(`/events/${id}/volunteer`); toast.success(r.data.volunteering ? "Thanks for volunteering! 🙌" : "You've withdrawn"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const gcal = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(e.title)}&dates=${gcalStamp(e.date)}/${gcalStamp(e.end || e.date)}&details=${encodeURIComponent(e.description || "")}&location=${encodeURIComponent(e.venue || e.location || e.online_link || "")}`;
  const icsUrl = `${API}/events/${id}/calendar.ics`;
  const isPast = new Date(e.date) < new Date();

  return (
    <article className="mx-auto max-w-4xl px-5 py-12">
      <Link to="/events" className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> All events</Link>

      <div className="overflow-hidden rounded-3xl border border-border/60 bg-card shadow-sm">
        <div className="relative h-56 bg-primary sm:h-72">
          {e.cover ? <img src={e.cover.startsWith("http") ? e.cover : fileUrl(e.cover)} alt={e.title} className="h-full w-full object-cover" />
            : <div className="grid h-full place-items-center"><CalendarStar size={56} className="text-primary-foreground/50" weight="duotone" /></div>}
        </div>
        <div className="p-6 sm:p-8">
          <div className="flex flex-wrap gap-1.5">
            {e.event_type && <Badge variant="secondary" className="rounded-full font-normal">{e.event_type}</Badge>}
            {e.event_category && <Badge variant="outline" className="rounded-full font-normal">{e.event_category}</Badge>}
            {e.mode && <Badge variant="outline" className="rounded-full font-normal">{e.mode}</Badge>}
            {isPast && <Badge className="rounded-full bg-muted font-normal text-muted-foreground">Past event</Badge>}
            {e.recurring && <Badge variant="outline" className="rounded-full gap-1 font-normal"><ArrowsClockwise size={12} /> Repeats yearly</Badge>}
          </div>
          <h1 className="mt-3 font-heading text-3xl font-light leading-tight tracking-tight sm:text-4xl" data-testid="event-title">{e.title}</h1>
          {e.institution_name && (
            <Link to={e.institution_slug ? `/site/${e.institution_slug}` : "#"} className="mt-2 inline-flex items-center gap-1.5 text-sm text-secondary hover:underline"><Buildings size={15} /> {e.institution_name}</Link>
          )}

          <div className="mt-5 grid gap-3 text-sm text-foreground/90 sm:grid-cols-2">
            <div className="flex items-center gap-2"><Clock size={16} className="text-secondary" /> {fmtDate(e.date)}{e.end ? ` — ${fmtDate(e.end)}` : ""}</div>
            {e.country && <div className="flex items-center gap-2"><Globe size={16} className="text-secondary" /> {e.country}</div>}
            {(e.venue || e.location) && <div className="flex items-center gap-2"><MapPin size={16} className="text-secondary" /> {[e.venue || e.location, e.room, e.campus].filter(Boolean).join(", ")}</div>}
            {e.online_link && <a href={e.online_link} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-secondary hover:underline"><VideoCamera size={16} /> Join online</a>}
            {e.organizer && <div className="flex items-center gap-2"><User size={16} className="text-secondary" /> {e.organizer}{e.organizer_unit ? ` · ${e.organizer_unit}` : ""}</div>}
            {e.contact_email && <a href={`mailto:${e.contact_email}`} className="flex items-center gap-2 text-secondary hover:underline"><EnvelopeSimple size={16} /> {e.contact_email}</a>}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><Users size={15} /> {e.rsvp_count || 0} going</span>
            <span className="flex items-center gap-1.5"><Heart size={15} /> {e.interested_count || 0} interested</span>
            {e.entry_fee && <span className="flex items-center gap-1.5"><Ticket size={15} /> {e.entry_fee}</span>}
            {e.max_participants > 0 && <span>Max {e.max_participants}</span>}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            {e.registration_enabled && !isPast && (
              <Button onClick={register} variant={e.my_rsvp ? "default" : "default"} className="rounded-full gap-1.5" data-testid="event-register-btn">
                {e.my_rsvp ? <><Check size={16} /> Registered</> : "Register"}
              </Button>
            )}
            <Button onClick={interested} variant="outline" className={`rounded-full gap-1.5 ${e.my_interested ? "border-secondary text-secondary" : ""}`} data-testid="event-interested-btn">
              <Heart size={16} weight={e.my_interested ? "fill" : "regular"} /> {e.my_interested ? "Interested" : "I'm interested"}
            </Button>
            <a href={gcal} target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-full gap-1.5" data-testid="event-gcal-btn"><GoogleLogo size={16} /> Google Calendar</Button></a>
            <a href={icsUrl}><Button variant="outline" className="rounded-full gap-1.5" data-testid="event-ics-btn"><DownloadSimple size={16} /> .ics</Button></a>
            <ShareButton targetPath={`/events/${id}`} entityType="event" entityId={id} title={e.title} />
            {e.can_manage && e.institution_id && <InviteAlumniDialog eventId={id} institutionName={e.institution_name} />}
            {e.can_manage && <Button onClick={duplicate} variant="outline" className="rounded-full gap-1.5" data-testid="event-duplicate-btn"><CopySimple size={16} /> Duplicate / Republish</Button>}
          </div>
        </div>
      </div>

      {e.description && (
        <section className="mt-8">
          <h2 className="font-heading text-2xl font-light">About this event</h2>
          <p className="mt-3 whitespace-pre-line leading-relaxed text-foreground/90">{e.description}</p>
        </section>
      )}

      {(e.prizes || e.volunteers_needed > 0 || e.age_group || (e.audience || []).length > 0) && (
        <section className="mt-8 grid gap-4 sm:grid-cols-2">
          {e.prizes && <div className="rounded-2xl border border-border/60 p-5"><p className="flex items-center gap-1.5 text-sm font-medium text-primary"><Trophy size={16} /> Prizes</p><p className="mt-1 text-sm text-muted-foreground">{e.prizes}</p></div>}
          {e.volunteers_needed > 0 && (
            <div className="rounded-2xl border border-secondary/30 bg-accent/40 p-5" data-testid="event-volunteer-card">
              <p className="flex items-center gap-1.5 text-sm font-medium text-primary"><HandHeart size={16} /> Volunteers needed</p>
              <p className="mt-1 text-sm text-muted-foreground">{e.volunteer_count || 0} of {e.volunteers_needed} spots filled</p>
              {(e.volunteers || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {e.volunteers.slice(0, 8).map((v) => (
                    <span key={v.user_id} className="flex items-center gap-1.5 rounded-full border border-border/60 bg-card py-1 pl-1 pr-3 text-xs">
                      {v.avatar_file_id ? <img src={fileUrl(v.avatar_file_id)} alt={v.name} className="h-6 w-6 rounded-full object-cover" /> : <span className="grid h-6 w-6 place-items-center rounded-full bg-accent text-[10px]">{(v.name || "?")[0]}</span>}
                      {v.name}
                    </span>
                  ))}
                </div>
              )}
              {!isPast && (
                <Button onClick={volunteer} variant={e.my_volunteer ? "outline" : "secondary"} size="sm" className="mt-3 rounded-full gap-1.5" data-testid="event-volunteer-btn">
                  <HandHeart size={15} weight={e.my_volunteer ? "fill" : "regular"} /> {e.my_volunteer ? "Withdraw" : "Volunteer"}
                </Button>
              )}
            </div>
          )}
          {(e.audience || []).length > 0 && <div className="rounded-2xl border border-border/60 p-5"><p className="text-sm font-medium text-primary">Open to</p><p className="mt-1 text-sm text-muted-foreground">{e.audience.join(", ")}{e.age_group ? ` · ${e.age_group}` : ""}</p></div>}
        </section>
      )}

      {(e.speakers || []).length > 0 && (
        <section className="mt-8">
          <h2 className="font-heading text-2xl font-light">Speakers & guests</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {e.speakers.map((s, i) => <div key={i} className="rounded-xl border border-border/60 p-4"><div className="font-medium">{s.name}</div>{s.title && <div className="text-sm text-muted-foreground">{s.title}</div>}</div>)}
          </div>
        </section>
      )}

      {(e.photos?.length > 0 || e.video_urls?.length > 0 || e.google_album_url || user) && (
        <section className="mt-8">
          <h2 className="flex items-center gap-2 font-heading text-2xl font-light"><Images size={22} weight="duotone" className="text-secondary" /> Event memories</h2>
          {e.photos?.length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="photo-grid">
              {e.photos.map((p, i) => (
                <button key={p.id} onClick={() => setLightbox(i)} className="group relative overflow-hidden rounded-xl" data-testid={`photo-thumb-${p.id}`}>
                  <img src={fileUrl(p.file_id)} alt={p.caption || "Event"} className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-gradient-to-t from-black/70 to-transparent px-3 py-2 text-xs text-white">
                    <span className="flex items-center gap-1"><ThumbsUp size={12} weight="fill" /> {p.like_count || 0}</span>
                    <span className="flex items-center gap-1"><ChatCircle size={12} weight="fill" /> {p.comment_count || 0}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {(e.video_urls || []).map((v, i) => <a key={i} href={v} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="rounded-full gap-1.5"><VideoCamera size={14} /> Video {i + 1}</Button></a>)}
            {e.google_album_url && <a href={e.google_album_url} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="rounded-full gap-1.5"><Images size={14} /> Google Photos album</Button></a>}
          </div>
          {user && <EventMemoriesUploader eventId={id} googleAlbum={e.google_album_url} canManage={e.can_manage} onDone={load} />}
        </section>
      )}

      {lightbox !== null && e.photos?.[lightbox] && (
        <EventPhotoLightbox photos={e.photos} index={lightbox} onClose={() => setLightbox(null)} onNav={setLightbox} onChange={load} />
      )}

      {(e.attendees || []).length > 0 && (
        <section className="mt-8">
          <h2 className="font-heading text-2xl font-light">Who's going</h2>
          <div className="mt-3 flex flex-wrap gap-3">
            {e.attendees.map((a) => (
              <Link key={a.user_id} to={a.public_slug ? `/u/${a.public_slug}` : "#"} className="flex items-center gap-2 rounded-full border border-border/60 py-1 pl-1 pr-3">
                {a.avatar_file_id ? <img src={fileUrl(a.avatar_file_id)} alt={a.name} className="h-7 w-7 rounded-full object-cover" /> : <span className="grid h-7 w-7 place-items-center rounded-full bg-accent text-xs">{(a.name || "?")[0]}</span>}
                <span className="text-sm">{a.name}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <EventDiscussion eventId={id} />
      <div className="mt-8">
        <TalkVibes entityType="event" entityId={id} title={e.title}
          subtitle="Reunion & event talk — RSVP chatter, memories and photos." accent="event" />
      </div>
    </article>
  );
}
